import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useEffect, useState} from "react";
import referrals from '../../models/referrals'
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {Add, Refresh, Check, Delete} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import {Link as RouterLink, useNavigate, useSearchParams} from "react-router-dom";
import {ToggleButton, ToggleButtonGroup} from "@mui/material";
import Pagination from "../Pagination";
import {AclRouterLink} from "../ACL";
import IconButton from "@mui/material/IconButton";
import PageHeader from '../layout/PageHeader';
import FilterPanel from '../layout/FilterPanel';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';

export default function ReferralIndex() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [refreshId, setRefreshId] = useState(0)
    const [referralsData, setReferralsData] = useState(null)
    const {setShowLoader, showConfirm} = useRootHook()
    const navigate = useNavigate()
    const accordionExpanded = Array.from(searchParams).length > 0

    useEffect(() => {
        setShowLoader(true)
        const filters = searchParams ? Object.fromEntries(searchParams) : {}
        referrals.index(filters)
            .then((data) => {
                data && setReferralsData(data)
                setShowLoader(false)
            }).catch(() => setShowLoader(false))
    }, [searchParams, refreshId])

    const handleSearch = (event) => {
        event.preventDefault()
        const formData = new FormData(event.currentTarget)
        formData.forEach((value, key) => {
            searchParams.set(key, value.toString())
        })
        setSearchParams(searchParams)
    }

    const handleRefresh = () => {
        setRefreshId((prevId) => {
            return prevId + 1
        })
    }
    
    const enabledFilter = searchParams.get('enabled') || 'all'
    
    const handleEnabledFilterChange = (event, newValue) => {
        if (newValue === null) return
        const newParams = new URLSearchParams(searchParams)
        if (newValue === 'all') {
            newParams.delete('enabled')
        } else {
            newParams.set('enabled', newValue === 'active' ? 'true' : 'false')
        }
        setSearchParams(newParams)
    }

    const handleDelete = async (id) => {
        const confirmed = await showConfirm('Are you sure you want to delete this referral?', 'Delete Referral');
        if (!confirmed) return;

        setShowLoader(true);
        referrals.delete(id)
            .then(() => {
                setShowLoader(false);
                handleRefresh();
            })
            .catch(() => setShowLoader(false));
    };

    if (!referralsData) return null

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader
                title="Referrals"
                breadcrumbs={[['Dashboard', '/'], ['Referrals', '/referrals']]}
                actions={
                    <>
                        <Button to="/referrals/add" component={AclRouterLink} variant="contained" startIcon={<Add />}>
                            Add Referral
                        </Button>
                        <Button onClick={handleRefresh} variant="outlined" startIcon={<Refresh />}>
                            Refresh
                        </Button>
                    </>
                }
            />
        </Grid>
        <Grid size={12}>
            <FilterPanel defaultExpanded={accordionExpanded} title="Filters">
                <form onSubmit={handleSearch} noValidate autoComplete="off">
                    <Button type="submit" variant="contained" size="small">Search</Button>
                </form>
            </FilterPanel>
        </Grid>
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                <TableToolbar 
                    title="Referrals" 
                    actions={
                        <ToggleButtonGroup
                            value={enabledFilter}
                            size="small"
                            exclusive
                            onChange={handleEnabledFilterChange}
                            aria-label="Status filter"
                        >
                            <ToggleButton value="all">ALL</ToggleButton>
                            <ToggleButton value="active">Active</ToggleButton>
                            <ToggleButton value="inactive">Inactive</ToggleButton>
                        </ToggleButtonGroup>
                    }
                />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Enabled</TableCell>
                            <TableCell>Added</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {referralsData.referrals.map((row) => (
                            <TableRow key={`ref-${row.id}`}>
                                <TableCell>
                                    {row.id}
                                </TableCell>
                                <TableCell>
                                    <Link to={`edit?id=${row.id}`} component={RouterLink}>
                                        {row.username}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    {row.name}
                                </TableCell>
                                <TableCell>{row.enabled ? <Check fontSize="small" /> : ''}</TableCell>
                                <TableCell>{row.created ? helpers.getDateTime(row.created) : '—'}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleDelete(row.id)} color="error" size="small">
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Pagination url={'/referrals'} count={referralsData.count}/>
            </SectionCard>
        </Grid>
    </React.Fragment>
}

